import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Pause, RotateCcw, Timer } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const R = 88;
const C = 2 * Math.PI * R;

export const FocusTimer = ({ open, onOpenChange, label = "Focus session", minutes = 25, onComplete }) => {
  const total = minutes * 60;
  const [left, setLeft] = useState(total);
  const [endAt, setEndAt] = useState(null);
  const done = useRef(false);
  const cb = useRef(onComplete);
  cb.current = onComplete;
  useEffect(() => { if (open) { setLeft(total); setEndAt(null); done.current = false; } }, [open, total]);
  useEffect(() => {
    if (!endAt) return undefined;
    const t = setInterval(() => {
      const s = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setLeft(s);
      if (s === 0 && !done.current) {
        done.current = true;
        setEndAt(null);
        navigator.vibrate?.([200, 100, 200]);
        api.post("/focus", { minutes, label: label.slice(0, 140) }).catch(() => {});
        toast.success("Session complete. Nice focus. Take a 5-minute break");
        cb.current?.();
      }
    }, 500);
    return () => clearInterval(t);
  }, [endAt, minutes, label]);
  const running = Boolean(endAt);
  const mm = String(Math.floor(left / 60)).padStart(2, "0");
  const ss = String(left % 60).padStart(2, "0");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="focus-timer-card" className="bg-surface border border-gold-400/30 text-white rounded-3xl max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-3xl text-white"><Timer className="w-6 h-6 text-gold-300" />Focus</DialogTitle>
          <DialogDescription className="text-slate-600">{label}</DialogDescription>
        </DialogHeader>
        <div className="relative mx-auto my-2 w-56 h-56">
          <svg viewBox="0 0 200 200" className="w-full h-full -rotate-90">
            <circle cx="100" cy="100" r={R} fill="none" stroke="#1B2A4F" strokeWidth="10" />
            <circle cx="100" cy="100" r={R} fill="none" stroke={left === 0 ? "#34D399" : "#D9AE34"} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={C} strokeDashoffset={C * (left / total)} style={{ transition: "stroke-dashoffset .5s linear", filter: "drop-shadow(0 0 8px rgba(217,174,52,.45))" }} />
          </svg>
          <div className="absolute inset-0 grid place-items-center">
            <p data-testid="focus-timer-display" className="font-mono text-5xl font-bold text-white tabular-nums">{mm}:{ss}</p>
          </div>
        </div>
        <div className="flex justify-center gap-2">
          <button data-testid="focus-timer-toggle" onClick={() => (running ? (setEndAt(null)) : left > 0 && setEndAt(Date.now() + left * 1000))}
            className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-bold transition-colors ${running ? "bg-slate-100 text-white hover:bg-slate-200" : "bg-gold-400 text-black hover:bg-gold-300"}`}>
            {running ? <><Pause className="w-4 h-4" />Pause</> : <><Play className="w-4 h-4" />{left === total ? "Start" : left === 0 ? "Done" : "Resume"}</>}
          </button>
          <button data-testid="focus-timer-reset" onClick={() => { setEndAt(null); setLeft(total); done.current = false; }} aria-label="Reset" className="grid place-items-center w-11 h-11 rounded-full border border-slate-200 text-slate-600 hover:text-white"><RotateCcw className="w-4 h-4" /></button>
        </div>
        <p className="text-center text-xs text-slate-500">When the timer ends, this session is ticked off for you.</p>
      </DialogContent>
    </Dialog>
  );
};
